import { useEffect, useRef, useState } from 'react'
import { ShareButton } from './ShareButton.jsx'
import { SocialProofChip } from './SocialProofChip.jsx'

// UX40 (extended UX50): match BillCard's inline reveal timing.
const REVEAL_MS = 2500

function fmtDate(d) {
  if (!d) return ''
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function pct(n, t) {
  if (!t) return 0
  return Math.round((n / t) * 1000) / 10
}

export function CandidateCard({ item, tally, userVote, onVote, onViewMetrics, onShareQuote, isLoggedIn, activity }) {
  // UX50: inline dopamine reveal on candidate pick. Same card-local
  // overlay BillCard uses so the moment reads consistently across
  // card types: "You picked <name>" + "<pct>% of your district
  // picked the same." Auto-dismisses in 2.5s.
  const prevVoteRef = useRef(userVote)
  const [reveal, setReveal] = useState(null)
  useEffect(() => {
    if (userVote && userVote !== prevVoteRef.current) {
      // Snapshot the stat at the moment of the vote so a tally update
      // mid-reveal doesn't flicker the number.
      const isUserResident = item.resident === true
      const addToRes = isUserResident ? 1 : 0
      let totalRes = 0
      let choiceRes = 0
      for (const c of item.candidates) {
        const r = (tally?.[c.name]?.resident || 0) + (c.name === userVote ? addToRes : 0)
        totalRes += r
        if (c.name === userVote) choiceRes = r
      }
      const pctRes = totalRes ? Math.round((choiceRes / totalRes) * 100) : 0
      const isFirst = totalRes === addToRes
      setReveal({ choice: userVote, pct: pctRes, isUserResident, isFirst })
      const timer = setTimeout(() => setReveal(null), REVEAL_MS)
      prevVoteRef.current = userVote
      return () => clearTimeout(timer)
    }
    prevVoteRef.current = userVote
  }, [userVote, tally, item.resident, item.candidates])

  const totals = item.candidates.map((c) => {
    const t = tally?.[c.name] || { resident: 0, nonResident: 0 }
    return t.resident + t.nonResident
  })
  const totalAll = totals.reduce((a, b) => a + b, 0)
  const residentTotal = item.candidates.reduce(
    (s, c) => s + (tally?.[c.name]?.resident || 0), 0)
  const nonResidentTotal = item.candidates.reduce(
    (s, c) => s + (tally?.[c.name]?.nonResident || 0), 0)

  return (
    <article className="card card-candidate" style={{ position: 'relative' }} data-poll-id={item.id}>
      {reveal && (
        <div className="bill-card-reveal" aria-live="polite">
          <div className="bill-card-reveal-choice">
            <span className="bill-card-reveal-emoji" aria-hidden="true">👍</span>
            <span>
              You picked <strong>{reveal.choice}</strong>
            </span>
          </div>
          {!reveal.isFirst && (
            <div className="bill-card-reveal-stat">
              <span className="bill-card-reveal-pct">{reveal.pct}%</span>
              <span className="bill-card-reveal-stat-text">
                of {reveal.isUserResident ? 'your district' : 'residents'} picked the same
              </span>
            </div>
          )}
          {reveal.isFirst && (
            <div className="bill-card-reveal-stat-text">
              You&rsquo;re the first voter {reveal.isUserResident ? 'in your district' : 'on this poll'}.
            </div>
          )}
        </div>
      )}
      <div className="card-head">
        <div className="card-meta">
          <span className="badge candidate">Candidate · {item.levelLabel}</span>
          {item.office && <span className="bill-id">{item.office}</span>}
          {item.resident === false && (
            <span className="badge nonresident">Non-resident view</span>
          )}
          {/* UX45: social-proof chip. */}
          <SocialProofChip activity={activity} />
        </div>
      </div>

      <h3 className="card-title">{item.title || 'Candidate race'}</h3>
      {item.electionDate && (
        <p className="card-summary">Election date: {fmtDate(item.electionDate)}</p>
      )}

      <div className="vote-block">
        {item.candidates.map((c) => {
          const t = tally?.[c.name] || { resident: 0, nonResident: 0 }
          const total = t.resident + t.nonResident
          const residentWidth = totalAll ? (t.resident / totalAll) * 100 : 0
          const nonResidentWidth = totalAll ? (t.nonResident / totalAll) * 100 : 0
          const voted = userVote === c.name

          return (
            <div className="candidate-row" key={c.name}>
              <div className="bar-wrap">
                <div>
                  <span className="candidate-name">{c.name}</span>
                  {c.party && <span className="candidate-party">· {c.party}</span>}
                </div>
                <div className="bar-track">
                  <span
                    className="bar-segment"
                    style={{ width: `${residentWidth}%`, background: 'var(--granite)' }}
                  />
                  <span
                    className="bar-segment"
                    style={{ width: `${nonResidentWidth}%`, background: 'var(--dusk)' }}
                  />
                </div>
                {totalAll > 0 && (
                  <div className="bar-labels">
                    <span><strong>{pct(t.resident, total)}%</strong> resident</span>
                    <span><strong>{pct(t.nonResident, total)}%</strong> non-resident</span>
                    <span>{total} votes</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className={`vote-btn candidate ${voted ? 'voted' : ''}`}
                onClick={() => onVote?.(item, c.name)}
                aria-disabled={!isLoggedIn || undefined}
                aria-pressed={voted}
              >
                {voted ? 'Voted' : 'Back'}
              </button>
            </div>
          )
        })}
        {totalAll === 0 && (
          <div className="zero-vote-hint">Be the first to vote.</div>
        )}
      </div>

      <div className="card-foot">
        <span>
          {totalAll} votes · {residentTotal} resident · {nonResidentTotal} non-resident
        </span>
        <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
          {item.endsAt ? `Closes ${fmtDate(item.endsAt)}` : `Posted ${fmtDate(item.postedAt)}`}
          <ShareButton item={item} tally={tally} userVote={userVote} isLoggedIn={isLoggedIn} onPostQuote={onShareQuote} />
        </span>
      </div>

      {onViewMetrics && (
        <div className="card-actions">
          <button
            type="button"
            className="card-toggle"
            onClick={() => onViewMetrics(item.id)}
          >
            Detailed metrics →
          </button>
        </div>
      )}
    </article>
  )
}
